import React, { useState } from "react";

const REQUIRED_FIELDS = [
  { key: "firstName", placeholder: "First name *", half: true },
  { key: "lastName", placeholder: "Last name *", half: true },
  { key: "jobTitle", placeholder: "Job title *", half: false },
  { key: "email", placeholder: "Email *", type: "email", half: false },
];

/**
 * Inline ad-hoc contact form (Innovation-Lab pattern). Additive — does not clear existing chips.
 * Uses indigo styling on both VQQ and Assessment request forms.
 */
export default function RequestContactInlineForm({ onAdd, onCancel }) {
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    jobTitle: "",
    email: "",
  });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const isEmpty = (key) => !String(values[key] ?? "").trim();

  const showError = (key) =>
    (touched[key] || submitAttempted) && isEmpty(key);

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const handleAdd = () => {
    setSubmitAttempted(true);
    const missing = REQUIRED_FIELDS.some((f) => isEmpty(f.key));
    if (missing) return;

    const email = values.email.trim().toLowerCase();
    onAdd?.({
      id: `ad-hoc-${email}-${Date.now()}`,
      email,
      first_name: values.firstName.trim(),
      last_name: values.lastName.trim(),
      job_title: values.jobTitle.trim(),
      title: values.jobTitle.trim(),
      displayName: `${values.firstName.trim()} ${values.lastName.trim()}`.trim(),
      displayTitle: values.jobTitle.trim(),
      isCategoryPrimary: false,
      isAdHoc: true,
    });

    setValues({ firstName: "", lastName: "", jobTitle: "", email: "" });
    setTouched({});
    setSubmitAttempted(false);
  };

  const renderField = (field) => (
    <div
      key={field.key}
      className={`flex rounded-md shadow-sm ring-1 ring-inset ${
        showError(field.key) ? "ring-red-300" : "ring-gray-200"
      } focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600`}
    >
      <input
        type={field.type ?? "text"}
        placeholder={field.placeholder}
        value={values[field.key]}
        onChange={(e) =>
          setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
        }
        onBlur={() => handleBlur(field.key)}
        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
      />
    </div>
  );

  return (
    <div className="mt-4 space-y-3">
      <div className="flex gap-4">
        <div className="flex-1">{renderField(REQUIRED_FIELDS[0])}</div>
        <div className="flex-1">{renderField(REQUIRED_FIELDS[1])}</div>
      </div>
      {renderField(REQUIRED_FIELDS[2])}
      {renderField(REQUIRED_FIELDS[3])}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={handleAdd}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Add Contact
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
